import React, {useState, useEffect} from 'react';
import Loader from './loader';

const {
   hoc: {withExplicitProps},
   util: {executeAfter},
   hooks: {usePromise, useBreakpoint, useSelector, useFeaturedMedia, useUnique, useHotkeys},
   css: {prefixCSS, styleToCSS},
   components: {generic:{Image, RawHTML}},
   wpQuery
} = polyphony;

const jawnSVG =
    <svg role="none" height={33} viewBox="0 0 407.437 407.437" fill='currentColor' style={{enableBackground: 'new 0 0 407.437 407.437', cursor: 'pointer'}}>
        <polygon points="386.258,91.567 203.718,273.512 21.179,91.567 0,112.815 203.718,315.87 407.437,112.815 "/>
    </svg>;

const SlideShow = withExplicitProps(({name}) => {
    const secondsPerSlide = 5;
    const [page, setPage] = useState(0);
    const isMobile = useBreakpoint() === 'mobile';
    const textMarginLeft = useSelector(s => s.measurements.slider_text_margin_left);
    const titleFontSize = useSelector(s => s.measurements.slider_title_font_size);
    const taglineFontSize = useSelector(s => s.measurements.slider_tagline_font_size);
    const unique = useUnique();

    const {loading, result} = usePromise(() =>
        wpQuery('/', {post_type: 'slide', taxonomy: 'slideshows', term: name, orderby: 'slideshow'})
    , []);

    let slides = [];
    let slide = undefined;
    if (!loading) {
        slides = [...result.posts].map(c => {
            let r = {...c, meta: {...c.meta, slide_meta: JSON.parse(c.meta.slide_meta) || {}}};
            if (!r.meta.slide_meta.coordinate) {
                r.meta.slide_meta.coordinate = {};
            }
            return r;
        });
        slides.sort((a, b) => a.meta.slide_meta.order > b.meta.slide_meta.order ? 1 : -1);
        slide = slides[page] || {};
    }

    useEffect(() => {
        if (!loading) {
            let numSlides = slides.length;
            const duration = slide.meta.slide_meta.duration || secondsPerSlide;
            return executeAfter(() => {
                setPage(page >= numSlides - 1 ? 0 : page + 1);
            }, duration * 1000);
        }
        return () => {};
    }, [page, result, loading]);

    const media = useFeaturedMedia(slide && slide.featured_media);

    if (loading) {
        return <Loader />;
    }

    if (result.total_count === 0) return null;

    let title = slide.title;
    let tagline = slide.excerpt;

    const {slide_meta} = slide.meta;
    let {href: slideHref, effect_type: effectType, duration, zoomFactor: scaleFactor = 2, coordinate: {x, y}} = slide_meta;
    if (!duration) duration = secondsPerSlide;

    let extraStyle = {};

    let css = `
@keyframes SlideshowTransition {
    0% {${styleToCSS(prefixCSS({opacity: 0}))}}
    5% {${styleToCSS(prefixCSS({opacity: 1}))}}
    95% {${styleToCSS(prefixCSS({opacity: 1}))}}
    100% {${styleToCSS(prefixCSS({opacity: 0}))}}
}
`;

    if (effectType === 'kenburns') {
        const trunc = x => Math.trunc(x * 1000) / 1000;
        const animName = 'KenBurns' + slide.ID;
        extraStyle = prefixCSS({
            animation: `${animName} ${Math.trunc(duration * 1000) / 1000}s forwards`,
            animationTimingFunction: 'linear'
        });
        let xtrans = trunc((x - 0.5) / 2);
        let ytrans = trunc((y - 0.5) / 2);
        const transform = `scale3d(${scaleFactor}, ${scaleFactor}, ${scaleFactor}) translate3d(${xtrans}%, ${ytrans}%, 0px)`;

        css += `
@keyframes ${animName} {
    0% {${styleToCSS(prefixCSS({
        opacity: 0
    }))}}
    5% {${styleToCSS(prefixCSS({
        opacity: 1
    }))}}
    95% {${styleToCSS(prefixCSS({
        transform,
        animationTimingFunction: 'linear',
        opacity: 1
    }))}}
    100% {${styleToCSS(prefixCSS({
        opacity: 0,
        animationTimingFunction: 'linear',
        transform
    }))}}
}`;
    }

    const styles = {
        div: prefixCSS({
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            overflow: 'hidden'
        }),
        imgAbsWrapper: {
            position: 'absolute',
            width: '100%',
            height: '100%',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'black',
            overflow: 'hidden'
        },
        imgRelWrapper: prefixCSS({
            position: 'relative',
            top: `calc(${-((scaleFactor - 1) / 2)} * 100%)`,
            left: `calc(${-((scaleFactor - 1) / 2)} * 100%)`,
            width: `calc(100% * ${scaleFactor})`,
            height: `calc(100% * ${scaleFactor})`,
            overflow: 'hidden'
        }),
        imgWrapper: prefixCSS({
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            position: 'absolute',
            overflow: 'hidden',
            width: `calc(100% / ${scaleFactor})`,
            height: `calc(100% / ${scaleFactor})`,
            transformOrigin: '0 0',
            transform: `translate3d(calc(100% * ${(scaleFactor - 1) / 2}), calc(100% * ${(scaleFactor - 1) / 2}), 0px)`,
            animation: `SlideshowTransition ${Math.trunc(duration * 1000) / 1000}s forwards`,
            ...extraStyle
        }),
        img: prefixCSS({
            objectFit: 'cover',
            height: '100%',
            width: '100%'
        }),
        titleOuterWrapper: prefixCSS({
            width: '100%',
            height: '100%',
            position: 'absolute'
        }),
        titleInnerWrapper: prefixCSS({
            padding: 20,
            paddingLeft: isMobile ? 20 : textMarginLeft,
            boxSizing: 'border-box',
            color: 'white',
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            position: 'absolute',
            display: 'flex',
            flexFlow: 'column nowrap',
            justifyContent: 'center',
            maxWidth: '100%'
        }),
        titleText: prefixCSS({
            wordWrap: 'anywhere',
            overflow: 'hidden'
        }),
        jawnWrapper: prefixCSS({
            bottom: 0,
            position: 'absolute',
            zIndex: 12,
            color: 'white',
            padding: 10,
            boxSizing: 'border-box'
        }),
        dotList: prefixCSS({
            right: 10,
            bottom: 0,
            padding: 10,
            boxSizing: 'border-box',
            position: 'absolute',
            display: 'flex',
            flexFlow: 'row nowrap'
        }),
        dot: {
            borderRadius: 11 / 2,
            margin: 5,
            cursor: 'pointer',
            width: 11,
            height: 11
        }
    };

    return (
        <div role="tabpanel" id={unique} style={styles.div}>
            <style>{css}</style>
            <div role="none" style={styles.imgAbsWrapper}>
                <div role="none" style={styles.imgRelWrapper}>
                    <div role="none" style={styles.imgWrapper}>
                        <Image lazy={false} media={media} style={styles.img}/>
                    </div>
                </div>
            </div>
            <a href={slideHref} style={styles.titleOuterWrapper}>
                <div role="none" style={styles.titleInnerWrapper}>
                    <h1 style={{...styles.titleText, fontSize: `calc(${titleFontSize})`}}>
                        <RawHTML>{title}</RawHTML>
                    </h1>
                    <h2 style={{...styles.titleText, fontSize: `calc(${taglineFontSize})`}}>
                        <RawHTML>{tagline}</RawHTML>
                    </h2>
                </div>
            </a>
            <a role="button" aria-label='Scroll Down' href="#content-wrapped-page" style={styles.jawnWrapper}>
                {jawnSVG}
            </a>
            <div role="tablist" style={styles.dotList}>
                {result.posts.map((_, i) => <Tab controls={unique} selected={i === page} onClick={() => setPage(i)} key={i} />)}
            </div>
        </div>
    );
}, ['name']);

const Tab = ({controls, selected, onClick}) => {
    const r = useHotkeys({
        Enter: onClick,
        Space: onClick
    });

    const color = selected ? 'white' : 'lightgray';

    return <a role="tab"
              tabIndex='0'
              aria-controls={controls}
              aria-selected={selected ? 'true' : 'false'}
              onClick={onClick}
              ref={r}
              style={{borderRadius: 11 / 2,
            margin: 5,
            cursor: 'pointer',
            width: 11,
            height: 11, background: color, color}} />;
};

SlideShow.displayName = 'SlideShow';

export default SlideShow;
